import { useCallback, useLayoutEffect, useState, useRef, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import { useCurrentValueGetter } from '../../../../hooks';
import config from '../../authentication/config';
import { gtmTracker, GTM_EVENTS } from '../../gtmTracker';
import { CHAT_AGENT_ACTION_TYPE } from '../config/chatAgentActionType';
import { CHAT_AGENT_COMPONENT } from '../config/chatAgentComponentGA';
import { COLLAPSED_WIDTH, EXPANDED_WIDTH } from '../config/chatAgentSideBarConfig';
import { CHAT_SERVICE_MODE } from '../config/chatServiceMode';
import { chatClientValueStore } from '../utils/chatClientValueStore';
import { getChatAgentGAValue } from '../utils/getChatAgentGAValue';
import { useChatAgentName } from './useChatAgentName';
import { useChatAgentSessionId } from './useChatAgentSessionId';
import { useChatKitService } from './useChatKitService';

export const useChatAgentService = () => {
    const [isReady, setReady] = useState(false);
    const [isOpened, setOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [sessionId, setSessionId, unsetSessionId] = useChatAgentSessionId();
    const [agentName, setAgentName, unsetAgentName] = useChatAgentName();
    const [container, setContainer] = useState(null);
    const [chatRenderMode, setChatRenderMode] = useState(null);
    const [client, setClient] = useState(config.getChatAgentClient());
    const [chatServiceMode, setChatServiceMode] = useState(CHAT_SERVICE_MODE.CHAT_AGENT);

    const {
        initialize: initializeChatKit,
        reset: resetChatKit,
        chatKitContainer,
        setChatKitContainer,
        chatKitContainerGetter,
    } = useChatKitService();

    chatClientValueStore.setValue(client);

    const mounted = useRef(true);
    const isConnected = useCurrentValueGetter(!!sessionId);
    const containerGetter = useCurrentValueGetter(container);
    const chatAgentWidth = useMemo(() => (isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH), [isCollapsed]);

    const expand = useCallback(() => {
        setIsCollapsed(false);
    }, []);

    const collapse = useCallback(() => {
        setIsCollapsed(true);
    }, []);

    const initialize = useCallback(
        ({ container, client = config.getChatAgentClient(), chatRenderMode, chatKitContainer } = {}) => {
            setOpen(isOpened => {
                if (!isOpened) {
                    gtmTracker.trackEvent(GTM_EVENTS.USABILITY, {
                        component: CHAT_AGENT_COMPONENT,
                        value: getChatAgentGAValue({ type: CHAT_AGENT_ACTION_TYPE.OPEN_CHAT }),
                    });
                }
                return true;
            });

            if (container) {
                setClient(client);
                setContainer(container);
                setChatRenderMode(chatRenderMode);
                setReady(true);
            }

            if (chatKitContainer) {
                setChatKitContainer(chatKitContainer);
                initializeChatKit({ chatKitContainer });
            }
        },
        [initializeChatKit, setChatKitContainer]
    );

    const open = useCallback(
        ({
            container,
            client = config.getChatAgentClient(),
            chatRenderMode,
            chatServiceMode = CHAT_SERVICE_MODE.CHAT_AGENT,
        } = {}) => {
            const effectiveContainerGetter =
                chatServiceMode === CHAT_SERVICE_MODE.CHATKIT ? chatKitContainerGetter : containerGetter;

            if (effectiveContainerGetter()) {
                expand();
                setChatServiceMode(chatServiceMode);
                return;
            }

            initialize({
                container,
                client,
                chatRenderMode,
                chatServiceMode,
                chatKitContainer,
            });
        },
        [containerGetter, chatKitContainerGetter, expand, initialize, chatKitContainer]
    );

    const close = useCallback(() => {
        gtmTracker.trackEvent(GTM_EVENTS.USABILITY, {
            component: CHAT_AGENT_COMPONENT,
            value: getChatAgentGAValue({ type: CHAT_AGENT_ACTION_TYPE.CLOSE_CHAT }),
        });

        collapse();
        unsetSessionId();
        unsetAgentName();
        setContainer(null);
        resetChatKit();
        setClient(config.getChatAgentClient());
        setOpen(false);
        setChatRenderMode(null);
    }, [collapse, unsetSessionId, unsetAgentName, resetChatKit]);

    useLayoutEffect(
        () => () => {
            mounted.current = false;
        },
        []
    );

    useEffectOnce(() => {
        unsetSessionId();
        unsetAgentName();
    });

    return {
        isOpened,
        open,
        close,
        initialize,
        container,
        isReady,
        setReady,
        client,
        sessionId,
        setSessionId,
        chatRenderMode,
        isConnected,
        isCollapsed,
        expand,
        collapse,
        chatAgentWidth,
        agentName,
        setAgentName,
        chatServiceMode,
    };
};
